import Modal from "react-bootstrap/Modal";
import { ReactSVG } from "react-svg";
import { NavDropdown, Table, Form, Row, Col, Button } from "react-bootstrap";
import srxlogowithtext from "../../../assets/images/SRX-logo-with-text.svg";
import exporticon from "../../../assets/images/export-icon.svg";
import excelicon from "../../../assets/images/excel-icon.svg";
import arrowdown from "../../../assets/images/angle-down.svg";
import useSensor from "../../../Hooks/useSensor";
import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import Loading from "../../../components/LoadingPage/Loading";

interface TemperatureLog {
  timeStamp: string;
  temp: number;
  alert: boolean;
  minTemp?: number;
  maxTemp?: number;
}

interface SensorLogsResponse {
  temperatureLogs: TemperatureLog[];
  higherTempLimit: number;
  lowerTempLimit: number;
}

const FREQUENCY_OPTIONS = [
  { value: "Daily", label: "Daily" },
  // { value: "Hourly", label: "Hourly" },
  // { value: 3, label: "Weekly" },
] as const;

export const TemperatureLogReportModel = ({
  showReportModal,
  onCloseReportModal,
  selectedSensor,
}: {
  showReportModal: boolean;
  onCloseReportModal: () => void;
  selectedSensor: {
    id: string;
    serialNumber: string;
    sensorName?: string;
    inCentigrade: boolean;
    lowerTempLimit?: number;
    higherTempLimit?: number;
    refInfo: any;
  } | null;
}) => {
  const [reportData, setReportData] = useState<any | null>(null);
  const [selectedFrequency, setSelectedFrequency] = useState<string>("Daily");
  const [startDate, setStartDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const {
    getSensorsLogAPI,
    getSensorsLogLoading,
    exportSensorLogsAPI,
    exportSensorLogsLoading,
  } = useSensor();

  const refInfo = useMemo(() => selectedSensor?.refInfo || {}, [selectedSensor]);

  const fetchReportData = async () => {
    if (!refInfo?.refrigeratorName) return;

    try {
      const response: SensorLogsResponse = await getSensorsLogAPI({
        RefrigeratorName: refInfo?.refrigeratorName || "",
        startDate: moment(startDate).toISOString(),
        endDate: moment(endDate).toISOString(),
        clientCode: localStorage.getItem("profileClientId") || "",
        dataFrequency: selectedFrequency,
      });
      setReportData(response);
    } catch (error) {
      console.error("Error fetching sensor logs:", error);
      setReportData(null);
    }
  };

  useEffect(() => {
    if (showReportModal && refInfo) {
      fetchReportData();
    }
  }, [showReportModal, refInfo]);

  const handleFrequencyChange = (e: any) => {
    setSelectedFrequency(e.target.value);
  };

  const getFrequencyLabel = (value: string) => {
    return FREQUENCY_OPTIONS.find((opt) => opt.value === value)?.label || "";
  };

  const handleViewReport = () => {
    fetchReportData();
  };

  const temperatureUnit = useMemo(
    () => (selectedSensor?.inCentigrade ? "°C" : "°F"),
    [selectedSensor?.inCentigrade]
  );

  const exportReport = async () => {
    if (!selectedSensor?.id) return;

    try {
      const response = await exportSensorLogsAPI({
        id: selectedSensor.id,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        frequency: selectedFrequency,
      });
      const url = URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${localStorage.getItem("profileClientId")?.toUpperCase() ?? ""}_Temperature_Log_Report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting report:", error);
    }
  };

  const recordCount = reportData?.length ?? 0;

  return (
    <Modal
      size="xl"
      id="trans-report-modal"
      show={showReportModal}
      onHide={onCloseReportModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h4>View Temperature Log</h4>
          {/* <div className="trans-report-action-wrapper">
            <div className="trans-report-action-box export-report-opt-box">
              <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
              <NavDropdown title="Export Report">
                <NavDropdown.Item onClick={() => exportReport()}>
                  <img src={excelicon} alt="" /> Excel
                </NavDropdown.Item>
              </NavDropdown>
              <ReactSVG className="svg-box" wrapper="span" src={arrowdown} />
            </div>
          </div> */}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {getSensorsLogLoading || exportSensorLogsLoading ? <Loading /> : null}
        <div className="trans-report-content-wrapper">
          <div className="trans-report-content-header">
            <div className="header-content">
              <h5>Temperature Log Report</h5>
              <p>
                {moment(startDate).format("MM/DD/YYYY")} - {moment(endDate).format("MM/DD/YYYY")} ({getFrequencyLabel(selectedFrequency)})
              </p>
            </div>
            <div className="header-logo-wrapper">
              <div className="header-logo-box srx-logo-box">
                <div className="trans-report-detail-box">
                  <label>Refrigerator Name</label>
                  <p>{refInfo?.refrigeratorName || "N/A"}</p>
                </div>
                <div className="trans-report-detail-box">
                  <label>Temperature Unit</label>
                  <p>{temperatureUnit || "N/A"}</p>
                </div>
                <div className="trans-report-detail-box">
                  <label>Lower Limit</label>
                  <p>
                    {reportData?.lowerTempLimit ?? selectedSensor?.lowerTempLimit ?? "N/A"}
                    {reportData?.lowerTempLimit || selectedSensor?.lowerTempLimit ? temperatureUnit : ""}
                  </p>
                </div>
                <div className="trans-report-detail-box">
                  <label>Upper Limit</label>
                  <p>
                    {reportData?.higherTempLimit ?? selectedSensor?.higherTempLimit ?? "N/A"}
                    {reportData?.higherTempLimit || selectedSensor?.higherTempLimit ? temperatureUnit : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="trans-report-detail-box-wrapper">
            <div className="trans-report-filters">
              <Row className="align-items-end g-3">
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      max={moment(endDate).subtract(1, "days").format("YYYY-MM-DD")}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      min={moment(startDate).add(1, "days").format("YYYY-MM-DD")}
                      max={moment().format("YYYY-MM-DD")}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label>Frequency</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedFrequency}
                      onChange={handleFrequencyChange}
                      style={{ minWidth: "120px" }}
                    >
                      {FREQUENCY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col xs="auto">
                  <Button variant="primary" onClick={handleViewReport}>
                    View Report
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="trans-report-table-wrapper" style={{ overflow: "auto", maxHeight: "350px" }}>
              <Table bordered={false} hover={true}>
                <thead>
                  <tr>
                    <th>Serial #</th>
                    <th>Log Date</th>
                    <th className="text-center">Average</th>
                    <th className="text-center">Min</th>
                    <th className="text-center">Max</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.length ? (
                    reportData.map((log: any, index: any) => (
                      <tr key={index}>
                        <td>{log?.serialNumber}</td>
                        <td>{log.sensorLogDateTime ? moment(log.sensorLogDateTime).format("MM/DD/YYYY") : "N/A"}</td>
                        <td className="text-center">{log.avg ? Number(log.avg).toFixed(2) : ""}</td>
                        <td className="text-center">{log.min ?? log.min}</td>
                        <td className="text-center">{log.max ?? log.max}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">No temperature logs found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="record-count">{recordCount} record(s)</div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
